import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ImagePlus, SendHorizontal, X } from 'lucide-react';
import {
  fetchAdminSupportConversation,
  fetchAdminSupportConversations,
  sendAdminSupportMessage,
  uploadSupportImages,
} from '../../utils/supportChat.js';
import '../../component-styles/SupportInbox.css';

function formatTimestamp(value) {
  if (!value) return '';

  try {
    return new Intl.DateTimeFormat(undefined, {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    }).format(new Date(value));
  } catch {
    return '';
  }
}

export default function SupportInbox() {
  const [conversations, setConversations] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState('');
  const [conversation, setConversation] = useState(null);
  const [draft, setDraft] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConversation, setLoadingConversation] = useState(false);
  const [sending, setSending] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [dragActive, setDragActive] = useState(false);
  const fileInputRef = useRef(null);
  const threadRef = useRef(null);

  const loadList = useCallback(async ({ silent = false } = {}) => {
    if (!silent) setLoadingList(true);

    try {
      const data = await fetchAdminSupportConversations();
      const nextList = Array.isArray(data.conversations) ? data.conversations : [];
      setConversations(nextList);

      if (!selectedUserId && nextList[0]?.userId) {
        setSelectedUserId(nextList[0].userId);
      }
    } catch (err) {
      setError(err.message || 'Failed to load support inbox');
    } finally {
      if (!silent) setLoadingList(false);
    }
  }, [selectedUserId]);

  const loadConversation = useCallback(async (userId, { silent = false } = {}) => {
    if (!userId) {
      setConversation(null);
      return;
    }

    if (!silent) setLoadingConversation(true);

    try {
      const data = await fetchAdminSupportConversation(userId);
      setConversation(data.conversation || null);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to load conversation');
    } finally {
      if (!silent) setLoadingConversation(false);
    }
  }, []);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    if (!selectedUserId) return;
    loadConversation(selectedUserId);
  }, [loadConversation, selectedUserId]);

  useEffect(() => {
    const interval = window.setInterval(() => {
      loadList({ silent: true });
      if (selectedUserId) {
        loadConversation(selectedUserId, { silent: true });
      }
    }, 10000);

    return () => window.clearInterval(interval);
  }, [loadConversation, loadList, selectedUserId]);

  useEffect(() => {
    if (!threadRef.current) return;
    threadRef.current.scrollTop = threadRef.current.scrollHeight;
  }, [conversation?.messages?.length]);

  const selectedSummary = useMemo(
    () => conversations.find((item) => item.userId === selectedUserId) || null,
    [conversations, selectedUserId]
  );
  const customerLabel =
    selectedSummary?.customer?.companyName ||
    selectedSummary?.customer?.name ||
    selectedSummary?.customer?.email ||
    'Customer';

  async function handleFilesSelected(event) {
    const files = event.target.files;
    if (!files?.length) return;

    try {
      setUploading(true);
      const uploaded = await uploadSupportImages(files);
      setAttachments((prev) => [...prev, ...uploaded]);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
      event.target.value = '';
    }
  }

  async function handleDrop(event) {
    event.preventDefault();
    setDragActive(false);
    const files = event.dataTransfer?.files;
    if (!files?.length) return;

    try {
      setUploading(true);
      const uploaded = await uploadSupportImages(files);
      setAttachments((prev) => [...prev, ...uploaded]);
      setError('');
    } catch (err) {
      setError(err.message || 'Failed to upload image');
    } finally {
      setUploading(false);
    }
  }

  function removeAttachment(attachmentId) {
    setAttachments((prev) => prev.filter((item) => item.id !== attachmentId));
  }

  function handleComposerKeyDown(event) {
    if (event.key !== 'Enter' || event.shiftKey) return;
    event.preventDefault();
    handleSend(event);
  }

  async function handleSend(event) {
    event.preventDefault();
    if (!selectedUserId || sending || uploading) return;

    const body = draft.trim();
    if (!body && attachments.length === 0) return;

    try {
      setSending(true);
      const data = await sendAdminSupportMessage(selectedUserId, { body, attachments });
      setConversation(data.conversation || null);
      setDraft('');
      setAttachments([]);
      setError('');
      await loadList({ silent: true });
    } catch (err) {
      setError(err.message || 'Failed to send reply');
    } finally {
      setSending(false);
    }
  }

  return (
    <div className="support-inbox">
      <aside className="support-inbox-sidebar">
        <div className="support-inbox-title">
          <h2>Support Inbox</h2>
          <p>Customer conversations and admin replies.</p>
        </div>

        <div className="support-inbox-list">
          {loadingList ? (
            <p className="support-inbox-empty">Loading conversations...</p>
          ) : conversations.length === 0 ? (
            <p className="support-inbox-empty">No live conversations yet.</p>
          ) : (
            conversations.map((item) => (
              <button
                type="button"
                key={item.userId}
                className={`support-inbox-item ${item.userId === selectedUserId ? 'active' : ''}`}
                onClick={() => setSelectedUserId(item.userId)}
              >
                <div className="support-inbox-item-head">
                  <strong>{item.customer?.companyName || item.customer?.name || item.userId}</strong>
                  <span>{formatTimestamp(item.lastMessageAt)}</span>
                </div>
                <p>{item.lastMessagePreview || 'No preview available'}</p>
                <div className="support-inbox-item-foot">
                  <span>{item.customer?.email || 'No email'}</span>
                  {item.unreadForAdmin > 0 ? <b>{item.unreadForAdmin} new</b> : null}
                </div>
              </button>
            ))
          )}
        </div>
      </aside>

      <section className="support-inbox-thread">
        <header className="support-inbox-thread-header">
          <div>
            <h3>{selectedSummary?.customer?.companyName || selectedSummary?.customer?.name || 'Select a conversation'}</h3>
            <p>{selectedSummary?.customer?.email || 'Choose a thread to reply.'}</p>
          </div>
          {selectedSummary?.status ? <span className="support-inbox-status">{selectedSummary.status}</span> : null}
        </header>

        <div className="support-inbox-messages" ref={threadRef}>
          {loadingConversation ? (
            <p className="support-inbox-empty">Loading thread...</p>
          ) : !conversation ? (
            <p className="support-inbox-empty">Select a conversation to view messages.</p>
          ) : (
            (conversation.messages || []).map((message) => {
              const isAdmin = message.authorType === 'admin';
              const displayName = isAdmin ? 'Admin' : customerLabel;
              return (
                <article
                  key={message.messageId}
                  className={`support-inbox-message ${isAdmin ? 'admin' : 'user'}`}
                >
                  <div className="support-inbox-bubble">
                    <div className="support-inbox-meta">
                      <strong>{displayName}</strong>
                      <time>{formatTimestamp(message.createdAt)}</time>
                    </div>
                    {message.body ? <p>{message.body}</p> : null}
                    {Array.isArray(message.attachments) && message.attachments.length > 0 ? (
                      <div className="support-inbox-images">
                        {message.attachments.map((attachment) => (
                          <a
                            key={attachment.id || attachment.url}
                            href={attachment.url}
                            target="_blank"
                            rel="noreferrer"
                          >
                            <img src={attachment.url} alt={attachment.name || 'Attachment'} />
                          </a>
                        ))}
                      </div>
                    ) : null}
                  </div>
                </article>
              );
            })
          )}
        </div>

        <form
          className="support-inbox-composer"
          onSubmit={handleSend}
          onDragOver={(event) => {
            event.preventDefault();
            setDragActive(true);
          }}
          onDragLeave={(event) => {
            event.preventDefault();
            setDragActive(false);
          }}
          onDrop={handleDrop}
          style={dragActive ? { outline: '2px dashed #7ca65c', outlineOffset: 4 } : undefined}
        >
          {attachments.length > 0 ? (
            <div className="support-inbox-attachments">
              {attachments.map((attachment) => (
                <button
                  type="button"
                  key={attachment.id}
                  className="support-inbox-attachment-chip"
                  onClick={() => removeAttachment(attachment.id)}
                >
                  {attachment.name}
                  <X size={12} />
                </button>
              ))}
            </div>
          ) : null}

          <textarea
            rows={3}
            placeholder="Reply to the customer..."
            value={draft}
            onChange={(event) => setDraft(event.target.value)}
            onKeyDown={handleComposerKeyDown}
            disabled={!selectedUserId}
          />

          {error ? <p className="support-inbox-error">{error}</p> : null}

          <div className="support-inbox-actions">
            <input
              ref={fileInputRef}
              type="file"
              accept="image/*"
              multiple
              hidden
              onChange={handleFilesSelected}
            />
            <button
              type="button"
              className="ghost"
              onClick={() => fileInputRef.current?.click()}
              disabled={!selectedUserId || sending || uploading}
            >
              <ImagePlus size={16} />
              {uploading ? 'Uploading...' : 'Add image'}
            </button>
            <button type="submit" disabled={!selectedUserId || sending || uploading}>
              <SendHorizontal size={16} />
              {sending ? 'Sending...' : 'Send reply'}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}
